// frontend/src/pages/PersonaHub.js
//
// /for — the hub for the persona pages (21 as of 2026-08-18).
//
// TWO REASONS THIS EXISTS, NEITHER OF THEM RANKING.
//
// 1. /for RETURNED 403. The prerenderer writes build/for/<slug>/index.html, so
//    /for is a real directory with no index file — and htaccess.template sets
//    Options -Indexes, which turns that into a permission error rather than a
//    404. The parent path of fifteen pages was answering crawlers with a 403.
//    Emitting a real page here fixes it, because Apache serves a file when one
//    exists.
//
// 2. THE PERSONAS HAD NO PARENT. Every one was reachable only from the footer.
//    A hub gives them descriptive internal links and a crawl path, which is
//    what Google uses to discover and prioritise — and as of 2026-08-17 the
//    persona pages are discovered but not ranking, so discovery is the live
//    problem.
//
// Deliberately NOT built to rank: `social media marketing for business` is
// KD 74, `social media marketing by industry` has no measurable volume. Chasing
// either would mean writing a page for a term we cannot win. The honest job is
// navigation.
//
// Generated from personas.json, so a new persona appears here automatically
// rather than needing a second edit.
import '../custom.css'

import React from 'react'
import { Container, Row, Col, Button } from 'react-bootstrap'
import { CheckCircleFill } from 'react-bootstrap-icons'

import Seo, { SITE_URL } from '../components/Seo'
import personas from '../data/personas.json'

const URL = '/for'

// Grouping is editorial: a long flat list is a link farm, grouped it is a
// directory. Any slug not listed falls into "More industries", so adding a
// persona never breaks this page.
const GROUPS = [
  ['Local and service businesses', ['salons', 'restaurants', 'gyms', 'dentists', 'chiropractors', 'med-spas', 'car-dealerships', 'contractors', 'plumbers', 'bakeries']],
  ['Professional services',        ['law-firms', 'consultants', 'insurance-agents', 'financial-advisors', 'accountants', 'real-estate', 'agencies']],
  ['Creative and media',           ['photographers', 'musicians', 'authors']],
  ['Organisations',                ['nonprofits', 'churches', 'schools']],
  ['Everyone else',                ['small-business', 'startups']],
]

/** First sentence of the persona's subheading — the specific problem, not a label. */
function hook (p) {
  const s = (p.sub || '').trim()
  const m = s.match(/^(.+?[.!?])(\s|$)/)
  const out = m ? m[1] : s
  return out.length > 120 ? `${out.slice(0, 117).trimEnd()}…` : out
}

export default function PersonaHub () {
  const bySlug = Object.fromEntries(personas.map(p => [p.slug, p]))
  const grouped = GROUPS.map(([title, slugs]) => [title, slugs.map(s => bySlug[s]).filter(Boolean)])
    .filter(([, list]) => list.length)
  const placed = new Set(GROUPS.flatMap(([, s]) => s))
  const rest = personas.filter(p => !placed.has(p.slug))
  if (rest.length) grouped.push(['More industries', rest])

  return (
    <>
      <Seo
        title='Social Media Marketing by Industry'
        description={`Social media guides for ${personas.length} industries — what actually works, what to post, and a workflow that survives a normal working week.`}
        path={URL}
        jsonLd={[{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'Social media marketing by industry',
          url: `${SITE_URL}${URL}`,
          hasPart: personas.map(p => ({
            '@type': 'WebPage',
            name: p.h1,
            url: `${SITE_URL}/for/${p.slug}`,
          })),
        }]}
      />

      {/* ───────── HERO ───────── */}
      <section className='full-bleed py-5 bg-primary-soft'>
        <Container fluid='lg'>
          <Row className='justify-content-center text-center'>
            <Col lg={9}>
              <h1 className='display-5 fw-bold mb-3'>Social media marketing by industry</h1>
              <p className='lead mb-0'>
                What actually works in {personas.length} industries — the real constraints, what to
                post, and a workflow that survives a normal working week.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── THE POINT ───────── */}
      <section className='py-5'>
        <Container style={{ maxWidth: 760 }}>
          <p className='fs-5'>
            Generic social media advice fails because the constraints are not generic. A restaurant
            cannot post during service. A dental practice needs written consent before its best
            content can go anywhere. A gym's whole year turns on three weeks in January.
          </p>
          <p className='text-muted fs-5 mb-0'>
            Each guide below is written for one of those situations rather than adapted from a
            template — because the reason most social media plans fail is the specific thing about
            your week that makes them impossible.
          </p>
        </Container>
      </section>

      {/* ───────── THE DIRECTORY ───────── */}
      <section className='py-5 bg-light'>
        <Container fluid='lg' style={{ maxWidth: 1000 }}>
          {grouped.map(([title, list]) => (
            <div className='mb-5' key={title}>
              <h2 className='fw-bold h4 mb-3'>{title}</h2>
              <Row className='g-3'>
                {list.map(p => (
                  <Col md={6} lg={4} key={p.slug}>
                    <a href={`/for/${p.slug}`}
                       className='d-block p-3 rounded-3 text-decoration-none h-100'
                       style={{ border: '1px solid #e5e7eb', background: '#fff' }}>
                      <div className='fw-semibold mb-1'>{p.h1}</div>
                      <div className='text-muted small'>{hook(p)}</div>
                    </a>
                  </Col>
                ))}
              </Row>
            </div>
          ))}
        </Container>
      </section>

      {/* ───────── WHAT IS GREATLY SOCIAL (standing rule 4) ───────── */}
      <section className='py-5'>
        <Container fluid='lg' style={{ maxWidth: 880 }}>
          <h2 className='fw-bold mb-3'>What is Greatly Social?</h2>
          <p className='text-muted fs-5'>
            Greatly Social is a social media scheduler and content calendar. Describe your business
            once and AI drafts the month — captions and images — then it schedules to all eight
            networks from one calendar, so posting stops depending on you being free at the right
            moment.
          </p>
          <Row className='g-3 mb-4'>
            {[['Describe it once, get the month', 'AI drafts the posts; you edit rather than write.'],
              ['Eight networks, one post', 'A separate caption per network on the same post.'],
              ['A real content calendar', 'Drag to reschedule. Empty days are obvious.'],
              ['Link in bio included', 'A hosted page that updates with your latest posts.']].map(([t, b]) => (
              <Col md={6} key={t}>
                <div className='d-flex align-items-start gap-2'>
                  <CheckCircleFill className='text-success mt-1 flex-shrink-0' />
                  <div><div className='fw-semibold'>{t}</div><div className='text-muted small'>{b}</div></div>
                </div>
              </Col>
            ))}
          </Row>
          <Button variant='primary' size='lg' href='/register' className='me-2'>
            Start using Greatly Social free
          </Button>
          <Button variant='outline-primary' size='lg' href='/plans'>See plans from $9/month</Button>
        </Container>
      </section>

      {/* ───────── CTA ───────── */}
      <section className='full-bleed py-5 text-center bg-primary text-white'>
        <div className='mx-auto px-4' style={{ maxWidth: 720 }}>
          <h2 className='fw-bold mb-3'>At Greatly Social you can hand over the month, not the judgement</h2>
          <p className='lead mb-4'>
            Describe your business, edit what the AI drafts, and schedule it across eight networks.
            Free for 14 days, no card, plans from $9/month.
          </p>
          <Button variant='light' size='lg' href='/register' className='me-2 mb-2'>
            Start using Greatly Social free
          </Button>
          <Button variant='outline-light' size='lg' href='/calendar-view' className='mb-2'>
            See the calendar
          </Button>
        </div>
      </section>
    </>
  )
}
